/**
 * Product Gallery interactions (main image, thumbnails, lightbox)
 * Relies on lightGallery 2.x + lg-thumbnail, lg-zoom, lg-fullscreen plugins.
 */
var ProductGallery = (function () {
  "use strict";

  let isResetting = false;
  let changeTimeout = null;
  let lightboxInstance = null;
  let customCaptionElement = null;
  let captionObserver = null;
  let toggleButtonElement = null;
  let resizeListenerBound = false;
  let cachedComponentsHeight = 0;
  let componentsHeightValid = false;
  let cachedGalleryData = null;

  function invalidateGalleryCache() {
    cachedGalleryData = null;
  }

  function buildItemFromThumb(thumb) {
    var link = thumb.querySelector("a");
    var img = thumb.querySelector("img.foto_image");
    var subHtml = link ? link.getAttribute("data-sub-html") : "";
    var itemType = img ? img.getAttribute("data-type") : "";
    var videoSrc = img ? img.getAttribute("data-ory-src") : null;

    var item = {
      src: link ? link.getAttribute("href") : "",
      thumb: img ? img.getAttribute("src") : "",
      subHtml: subHtml
    };

    if (itemType === "VIDEO" && videoSrc) {
      item.video = {
        source: [{ src: videoSrc, type: "video/mp4" }],
        attributes: { preload: false, controls: true, playsinline: true }
      };
    }

    return item;
  }

  function buildGalleryData() {
    if (cachedGalleryData) {
      return cachedGalleryData;
    }

    var allThumbnails = Array.from(document.querySelectorAll(".image_gall_sub"));

    var visibleThumbnails = allThumbnails.filter(function(thumb) {
      var parent = thumb.closest(".image_gall");
      if (!parent) return true;
      return window.getComputedStyle(parent).display !== "none";
    });

    var hiddenThumbnails = allThumbnails.filter(function(thumb) {
      var parent = thumb.closest(".image_gall");
      if (!parent) return false;
      return window.getComputedStyle(parent).display === "none";
    });

    var sortedThumbnails = visibleThumbnails.concat(hiddenThumbnails);

    var items = [];
    var thumbIndexMap = new Map();

    sortedThumbnails.forEach(function(thumb, idx) {
      thumbIndexMap.set(thumb, idx);
      items.push(buildItemFromThumb(thumb));
    });

    cachedGalleryData = { items: items, thumbIndexMap: thumbIndexMap };
    return cachedGalleryData;
  }

  function getSelectedVariantId() {
    var checked = document.querySelector(".attribs input[type=\"checkbox\"]:checked");
    if (checked) {
      return checked.id.replace("param-", "");
    }
    var data = buildGalleryData();
    return data.variantIds[0] || "default";
  }

  function getCookie(name) {
    const encodedName = name + "=";
    const parts = document.cookie.split(";");
    for (let i = 0; i < parts.length; i++) {
      let c = parts[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(encodedName) === 0) {
        return c.substring(encodedName.length, c.length);
      }
    }
    return "";
  }

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie =
      name + "=" + value + "; expires=" + d.toUTCString() + "; path=/";
  }

  /**
   * Main image handling
   */
  function changeMainImage(thumbnailElement, skipDebounce) {
    if (!skipDebounce) {
      if (isResetting) {
        return;
      }

      if (changeTimeout) {
        clearTimeout(changeTimeout);
      }

      changeTimeout = setTimeout(function () {
        changeMainImageImmediate(thumbnailElement);
        changeTimeout = null;
      }, 10);
      return;
    }

    changeMainImageImmediate(thumbnailElement);
  }

  function changeMainImageImmediate(thumbnailElement) {
    const mainImg = document.getElementById("main-img");
    if (!mainImg) {
      return;
    }

    const img = thumbnailElement.querySelector("img.foto_image");
    if (!img) {
      return;
    }

    const link = thumbnailElement.querySelector("a");
    const bigImageUrl = link ? link.getAttribute("href") : null;
    const itemType = img.getAttribute("data-type");

    const imageContainer = document.querySelector(".product-gallery .image");
    const pictureElement = imageContainer
      ? imageContainer.querySelector("picture")
      : null;

    if (itemType === "VIDEO") {
      if (!imageContainer) {
        return;
      }

      const videoSrc = img.getAttribute("data-ory-src");
      if (!videoSrc) {
        return;
      }

      // Hide static image / picture
      if (pictureElement) {
        pictureElement.style.display = "none";
      }
      mainImg.style.display = "none";

      // Remove previous video if exists
      const existingVideo = imageContainer.querySelector("video.gallery-video");
      if (existingVideo) {
        existingVideo.remove();
      }

      const video = document.createElement("video");
      video.className = "gallery-video";
      video.controls = true;
      video.loop = true;
      video.playsInline = true;
      video.style.width = "100%";
      video.style.height = "100%";

      const mutedCookie = getCookie("videoMuted");
      if (mutedCookie) {
        video.muted = true;
      }

      const source = document.createElement("source");
      source.src = videoSrc;
      source.type = "video/mp4";
      video.appendChild(source);

      imageContainer.appendChild(video);

      video.addEventListener("volumechange", function (event) {
        if (event.target.muted) {
          setCookie("videoMuted", "true", 100);
        }
      });
    } else {
      // Remove video if present
      if (imageContainer) {
        const existingVideo = imageContainer.querySelector(
          "video.gallery-video",
        );
        if (existingVideo) {
          existingVideo.remove();
        }
      }

      if (pictureElement) {
        pictureElement.style.display = "block";
      }
      mainImg.style.display = "block";

      if (!bigImageUrl) {
        return;
      }

      mainImg.setAttribute("src", bigImageUrl);

      if (pictureElement) {
        const sources = pictureElement.querySelectorAll("source");
        // Extract version parameter if present
        const versionMatch = bigImageUrl.match(/\?v=([^&]+)/);
        const versionQuery = versionMatch ? '?v=' + versionMatch[1] : '';
        const baseUrl = bigImageUrl.substring(0, bigImageUrl.lastIndexOf("."));
        // Remove version from baseUrl if it was included
        const cleanBaseUrl = baseUrl.replace(/\?v=[^&]+$/, '');
        
        sources.forEach(function (source) {
          const type = source.getAttribute("type");
          let newSrcset = bigImageUrl;
          
          if (type === "image/avif") {
            newSrcset = cleanBaseUrl + ".avif" + versionQuery;
          } else if (type === "image/webp") {
            newSrcset = cleanBaseUrl + ".webp" + versionQuery;
          } else if (type === "image/jpeg") {
            newSrcset = cleanBaseUrl + ".jpg" + versionQuery;
          }
          
          source.setAttribute("srcset", newSrcset);
        });
      }
    }

    // Update active thumbnail highlight
    document.querySelectorAll(".image_gall_sub").forEach(function (thumb) {
      thumb.classList.remove("img_active");
    });
    thumbnailElement.classList.add("img_active");
  }

  /**
   * Custom caption (Model: ...)
   */
  function ensureCustomCaptionElement() {
    if (customCaptionElement && document.body.contains(customCaptionElement)) {
      return customCaptionElement;
    }

    const outer = document.querySelector(".lg-outer");
    if (!outer) {
      return null;
    }

    customCaptionElement = document.createElement("div");
    customCaptionElement.className = "pg-model-caption";
    outer.appendChild(customCaptionElement);
    return customCaptionElement;
  }

  function updateCustomCaption(index) {
    if (!lightboxInstance) {
      return;
    }

    const outer = document.querySelector(".lg-outer");
    if (outer) {
      outer.classList.add("pg-hide-default-caption");
    }

    const captionEl = ensureCustomCaptionElement();
    if (!captionEl) {
      return;
    }

    const items = lightboxInstance.galleryItems || [];
    const item = items[index];
    if (!item) {
      captionEl.innerHTML = "";
      captionEl.style.display = "none";
      return;
    }

    const content = item.subHtml || "";
    if (content.trim()) {
      captionEl.innerHTML = content;
      captionEl.style.display = "";
    } else {
      captionEl.innerHTML = "";
      captionEl.style.display = "none";
    }

    componentsHeightValid = false;

    scheduleCaptionPosition();
  }

  function positionCustomCaption() {
    if (!customCaptionElement) {
      return;
    }

    const outer = document.querySelector(".lg-outer");
    if (!outer) {
      customCaptionElement.style.bottom = "20px";
      return;
    }

    let baseOffset = 20;
    let additionalOffset = 0;

    if (outer.classList.contains("lg-components-open")) {
      if (!componentsHeightValid) {
        const components = outer.querySelector(".lg-components");
        if (components) {
          cachedComponentsHeight =
            components.offsetHeight ||
            components.getBoundingClientRect().height ||
            0;
          componentsHeightValid = true;
        }
      }
      additionalOffset = cachedComponentsHeight;
    } else {
      componentsHeightValid = false;
      cachedComponentsHeight = 0;
    }

    customCaptionElement.style.bottom = baseOffset + additionalOffset + "px";
  }

  function scheduleCaptionPosition() {
    requestAnimationFrame(positionCustomCaption);
    setTimeout(positionCustomCaption, 100);
  }

  function hideInlineClose() {
    const closeBtn = document.getElementById("close-big-image");
    if (closeBtn) {
      closeBtn.style.display = "none";
    }
  }

  function showInlineGallery() {
    const closeBtn = document.getElementById("close-big-image");
    if (closeBtn) {
      closeBtn.style.display = "block";
    }
  }

  function ensureInlineCloseHandler() {
    const closeBtn = document.getElementById("close-big-image");
    if (!closeBtn) {
      return;
    }

    closeBtn.addEventListener("click", function (event) {
      event.preventDefault();
      const modal = document.getElementById("modal-gallery");
      if (modal) {
        modal.classList.add("show");
        modal.style.display = "block";
      }
    });
  }

  function collapseThumbnails() {
    const outer = document.querySelector(".lg-outer");
    if (!outer) {
      return;
    }

    if (outer.classList.contains("lg-components-open")) {
      outer.classList.remove("lg-components-open");
    }

    scheduleCaptionPosition();
  }

  function observeLightboxChanges() {
    const outer = document.querySelector(".lg-outer");
    if (!outer) {
      return;
    }

    if (captionObserver) {
      captionObserver.disconnect();
    }

    let isUserAction = false;
    const toggleBtn = outer.querySelector(".lg-toggle-thumb");

    if (toggleBtn) {
      toggleBtn.addEventListener("mousedown", function () {
        isUserAction = true;
      });

      toggleBtn.addEventListener("touchstart", function () {
        isUserAction = true;
      });
    }

    captionObserver = new MutationObserver(function (mutations) {
      let shouldUpdate = false;
      mutations.forEach(function (mutation) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          shouldUpdate = true;

          if (!isUserAction && outer.classList.contains("lg-components-open")) {
            setTimeout(function () {
              outer.classList.remove("lg-components-open");
              isUserAction = false;
            }, 0);
          } else if (isUserAction) {
            setTimeout(function () {
              isUserAction = false;
            }, 100);
          }
        }
      });
      if (shouldUpdate) {
        scheduleCaptionPosition();
      }
    });

    captionObserver.observe(outer, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  function detachLightboxObservers() {
    if (captionObserver) {
      captionObserver.disconnect();
      captionObserver = null;
    }

    if (toggleButtonElement) {
      toggleButtonElement.removeEventListener("click", handleToggleToggle);
      toggleButtonElement = null;
    }

    if (resizeListenerBound) {
      window.removeEventListener("resize", scheduleCaptionPosition);
      resizeListenerBound = false;
    }
  }

  function handleToggleToggle() {
    componentsHeightValid = false;
    setTimeout(scheduleCaptionPosition, 350);
  }

  function ensureToggleHandler() {
    const outer = document.querySelector(".lg-outer");
    if (!outer) {
      return;
    }

    const toggleButton = outer.querySelector(".lg-toggle-thumb");
    if (!toggleButton) {
      return;
    }

    if (toggleButtonElement && toggleButtonElement !== toggleButton) {
      toggleButtonElement.removeEventListener("click", handleToggleToggle);
    }

    toggleButtonElement = toggleButton;
    toggleButtonElement.addEventListener("click", handleToggleToggle);
  }

  function cleanupCustomCaption() {
    if (customCaptionElement && customCaptionElement.parentNode) {
      customCaptionElement.parentNode.removeChild(customCaptionElement);
    }
    customCaptionElement = null;

    const outer = document.querySelector(".lg-outer");
    if (outer) {
      outer.classList.remove("pg-hide-default-caption");
    }

    componentsHeightValid = false;
    cachedComponentsHeight = 0;

    detachLightboxObservers();
  }

  /**
   * LightGallery integration
   */
  function prepareVideoItems() {
    const thumbnails = document.querySelectorAll(".image_gall_sub");
    thumbnails.forEach(function (thumbnail) {
      const img = thumbnail.querySelector("img.foto_image");
      const link = thumbnail.querySelector("a");

      if (!img || !link) {
        return;
      }

      const itemType = img.getAttribute("data-type");
      const videoSrc = img.getAttribute("data-ory-src");

      if (itemType === "VIDEO" && videoSrc) {
        const videoData = {
          source: [
            {
              src: videoSrc,
              type: "video/mp4",
            },
          ],
          attributes: {
            preload: false,
            controls: true,
            playsinline: true,
          },
        };
        link.setAttribute("data-video", JSON.stringify(videoData));
      }
    });
  }

  function initLightGallery() {
    var galleryElement = document.getElementById("gallery");
    if (!galleryElement || typeof lightGallery === "undefined") {
      return;
    }

    if (lightboxInstance) {
      lightboxInstance.destroy(true);
      lightboxInstance = null;
    }

    var data = buildGalleryData();

    lightboxInstance = lightGallery(galleryElement, {
      dynamic: true,
      dynamicEl: data.items,
      download: false,
      allowMediaOverlap: true,
      licenseKey: "B6D73588-8E61-4DC8-A5B1-A3AE12B33800",
      plugins: [lgThumbnail, lgZoom, lgFullscreen, lgVideo],
      thumbnail: true,
      toggleThumb: true,
      zoom: true,
      showZoomInOutIcons: true,
      actualSize: false,
      fullScreen: false,
      videoMaxWidth: "1920px",
      mobileSettings: {
        controls: false,
        showCloseIcon: true,
        download: false,
        showZoomInOutIcons: false,
      },
    });

    lightboxInstance.LGel.on("lgAfterOpen.productGallery", function () {
      updateCustomCaption(lightboxInstance.index);
      observeLightboxChanges();
      ensureToggleHandler();
      collapseThumbnails();
      setTimeout(collapseThumbnails, 120);
      hideInlineClose();
      ensureInlineCloseHandler();
      if (!resizeListenerBound) {
        window.addEventListener("resize", scheduleCaptionPosition);
        resizeListenerBound = true;
      }
    });

    lightboxInstance.LGel.on("lgAfterSlide.productGallery", function (event) {
      var index =
        event && event.detail && typeof event.detail.index === "number"
          ? event.detail.index
          : lightboxInstance.index;
      updateCustomCaption(index);
    });

    lightboxInstance.LGel.on("lgBeforeClose.productGallery", function () {
      cleanupCustomCaption();
      showInlineGallery();
    });
  }

  function openLightboxAt(index) {
    if (!lightboxInstance) {
      initLightGallery();
    }

    if (!lightboxInstance) {
      return;
    }

    lightboxInstance.openGallery(index);
  }

  /**
   * Thumbnail helpers
   */
  function getThumbIndex(thumbnailElement) {
    var data = buildGalleryData();
    return data.thumbIndexMap.get(thumbnailElement) || 0;
  }

  function getActiveThumbnailIndex() {
    const activeThumb = document.querySelector(".image_gall_sub.img_active");
    if (!activeThumb) {
      return 0;
    }
    return getThumbIndex(activeThumb);
  }

  /**
   * Gallery setup
   */
  function initThumbnails() {
    const thumbnails = document.querySelectorAll(".image_gall_sub");
    if (!thumbnails.length) {
      return;
    }

    thumbnails.forEach(function (thumbnail) {
      thumbnail.addEventListener("mouseover", function () {
        changeMainImage(this);
      });

      thumbnail.addEventListener("click", function (event) {
        event.preventDefault();
        changeMainImage(this, true);
        const index = getThumbIndex(this);
        openLightboxAt(index);
      });
    });

    const firstThumb = thumbnails[0];
    if (firstThumb && !document.querySelector(".image_gall_sub.img_active")) {
      firstThumb.classList.add("img_active");
    }
  }

  function initMainImageInteractions() {
    const mainImageContainer = document.querySelector(
      ".product-gallery .image",
    );
    const zoomIcon = document.getElementById("img-zoom-option_noswap");

    if (mainImageContainer) {
      mainImageContainer.removeEventListener("click", handleMainImageClick);
      mainImageContainer.addEventListener("click", handleMainImageClick);
    }

    if (zoomIcon) {
      zoomIcon.removeEventListener("click", handleMainImageClick);
      zoomIcon.addEventListener("click", handleMainImageClick);
    }
  }

  function handleMainImageClick(event) {
    event.preventDefault();
    const index = getActiveThumbnailIndex();
    openLightboxAt(index);
  }

  /**
   * Reset & init
   */
  function resetGallery(currentSku) {
    isResetting = true;

    if (changeTimeout) {
      clearTimeout(changeTimeout);
      changeTimeout = null;
    }

    const allThumbnails = Array.from(document.querySelectorAll(".image_gall_sub"));
    const thumbnails = allThumbnails.filter(function(thumb) {
      const parent = thumb.closest('.image_gall');
      if (!parent) return false;
      const style = window.getComputedStyle(parent);
      return style.display !== 'none';
    });

    if (!thumbnails.length) {
      isResetting = false;
      return;
    }

    let mainThumb = null;

    if (currentSku) {
      mainThumb = thumbnails.find(function (thumb) {
        const mainForAttr = thumb.getAttribute("data-main-for");
        if (!mainForAttr) return false;
        const mainForSkus = mainForAttr.split(',');
        return mainForSkus.indexOf(currentSku) !== -1;
      });
    }

    if (!mainThumb) {
      mainThumb = thumbnails[0];
    }

    if (mainThumb) {
      changeMainImage(mainThumb, true);
    }

    setTimeout(function () {
      isResetting = false;
    }, 100);
  }

  /**
   * Variant Gallery - identical to product gallery
   */
  function initVariantGallery() {
    document.body.addEventListener("click", function (e) {
      const trigger = e.target.closest(".variant-lg-trigger");
      if (!trigger) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const galleryGroup = trigger.getAttribute("data-variant-gallery");
      const allVariants = document.querySelectorAll(
        '[data-variant-gallery="' + galleryGroup + '"]',
      );

      let clickedIndex = 0;
      const dynamicElements = [];

      allVariants.forEach(function (variant, index) {
        if (variant === trigger) {
          clickedIndex = index;
        }
        const variantTitle = variant.getAttribute("data-variant-title") || "";
        const href = variant.getAttribute("href");
        const thumb = href;
        const videoSrc = variant.getAttribute("data-video-src");

        const element = {
          src: href,
          thumb: thumb,
          subHtml:
            '<p><span class="lg-model-number">' + variantTitle + "</span></p>",
        };

        if (videoSrc) {
          element.video = {
            source: [
              {
                src: videoSrc,
                type: "video/mp4",
              },
            ],
            attributes: {
              preload: false,
              controls: true,
              playsinline: true,
            },
          };
        }

        dynamicElements.push(element);
      });

      if (dynamicElements.length === 0) {
        return;
      }

      const tempElement = document.createElement("div");
      document.body.appendChild(tempElement);

      const lgInstance = lightGallery(tempElement, {
        dynamic: true,
        dynamicEl: dynamicElements,
        plugins: [lgThumbnail, lgZoom, lgFullscreen, lgVideo],
        thumbnail: true,
        toggleThumb: true,
        download: false,
        allowMediaOverlap: true,
        licenseKey: "B6D73588-8E61-4DC8-A5B1-A3AE12B33800",
        addClass: "lg-variant-gallery",
        zoom: true,
        showZoomInOutIcons: true,
        actualSize: false,
        fullScreen: false,
        videoMaxWidth: "1920px",
        mobileSettings: {
          controls: false,
          showCloseIcon: true,
          download: false,
          showZoomInOutIcons: false,
        },
      });

      const previousInstance = lightboxInstance;
      lightboxInstance = lgInstance;

      lgInstance.openGallery(clickedIndex);

      lgInstance.LGel.on("lgAfterOpen.variantGallery", function () {
        updateCustomCaption(lgInstance.index);
        observeLightboxChanges();
        ensureToggleHandler();
        setTimeout(ensureToggleHandler, 150);
        collapseThumbnails();
        setTimeout(collapseThumbnails, 120);
        hideInlineClose();
        if (!resizeListenerBound) {
          window.addEventListener("resize", scheduleCaptionPosition);
          resizeListenerBound = true;
        }
      });

      lgInstance.LGel.on("lgAfterSlide.variantGallery", function (event) {
        const index =
          event && event.detail && typeof event.detail.index === "number"
            ? event.detail.index
            : lgInstance.index;
        updateCustomCaption(index);
      });

      lgInstance.LGel.on("lgBeforeClose.variantGallery", function () {
        cleanupCustomCaption();
        showInlineGallery();
      });

      tempElement.addEventListener("lgAfterClose", function () {
        lightboxInstance = previousInstance;
        lgInstance.destroy();
        tempElement.remove();
      });
    });
  }

  function initGallery() {
    initThumbnails();
    initMainImageInteractions();
    initVariantGallery();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initGallery);
  } else {
    initGallery();
  }

  return {
    reset: resetGallery,
    changeImage: changeMainImage,
    initLightGallery: initLightGallery,
    invalidateCache: invalidateGalleryCache,
  };
})();
